
import { 
  Box, 
  Divider, 
  IconButton,
  Paper,
  Typography
} from '@mui/material'
import { 
  Timeline as AxisIcon,
  Close as CloseIcon,
  Tune as FormatIcon
} from '@mui/icons-material'
import CurveFormatEditor from './CurveFormatEditor'
import { AxisConfigEditor } from './AxisConfigEditor'
import type { ChartConfig, YAxisPosition, YAxisConfig, CurveStyling } from '@/interfaces/waveChart'
import { WaveChartDefaults } from '@/interfaces/waveChart'

// Default curve format using WaveChartDefaults
const defaultCurveFormat: CurveStyling = {
  color: WaveChartDefaults.colors[0],
  strokeThickness: WaveChartDefaults.strokeThickness,
  lineStyle: WaveChartDefaults.lineStyle,
  yAxisId: 'left',
  visible: true,
  opacity: WaveChartDefaults.opacity,
  interpolation: WaveChartDefaults.interpolation,
  showDataPoints: false,
  pointStyle: WaveChartDefaults.pointStyle,
  pointSize: WaveChartDefaults.pointSize,
}

interface FormatTarget {
  type: 'curve' | 'axis'
  chartId: string
  curveId?: string
  axisId?: YAxisPosition
}

export interface FormatPanelProps {
  /** Current formatting target */
  formatTarget: FormatTarget
  
  /** Array of chart configurations */
  charts: ChartConfig[]
  
  /** Callback when a curve's formatting changes */
  onCurveFormatChange?: (chartId: string, curveId: string, format: CurveStyling) => void
  
  /** Callback when axis configuration changes */
  onAxisConfigChange?: (chartId: string, axisId: YAxisPosition, config: YAxisConfig) => void
  
  /** Callback when a curve is removed */
  onCurveRemove?: (chartId: string, curveId: string) => void
  
  /** Callback to close the format panel */
  onClose?: () => void
}

/**
 * Format panel for editing curve styling and axis configuration
 * Shows in the lower 2/3 of the Charts tab when formatting is active
 */
export function FormatPanel({
  formatTarget,
  charts,
  onCurveFormatChange,
  onAxisConfigChange,
  onClose
}: FormatPanelProps) {
  
  // Find the target chart
  const chart = charts.find(c => c.id === formatTarget.chartId)
  
  if (!chart) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">Chart not found</Typography>
      </Box>
    )
  }

  const getTitle = (): string => {
    if (formatTarget.type === 'curve' && formatTarget.curveId) {
      // Extract curve name from curveId (format: "filename:curveName")
      const curveName = formatTarget.curveId.split(':').pop() || 'Unknown Curve'
      return `Format Curve: ${curveName}`
    } else if (formatTarget.type === 'axis' && formatTarget.axisId) {
      const axisNames: Record<YAxisPosition, string> = {
        left: 'LEFT Y-AXIS',
        right: 'RIGHT Y-AXIS',
        left2: 'LEFT Y-AXIS 2', 
        right2: 'RIGHT Y-AXIS 2'
      }
      return `Configure ${axisNames[formatTarget.axisId]}`
    }
    return 'Format Panel'
  }

  const getIcon = () => {
    return formatTarget.type === 'curve' ? <FormatIcon /> : <AxisIcon />
  }

  return (
    <Paper 
      elevation={0}
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        maxHeight: '100%'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon()}
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            {getTitle()}
          </Typography>
        </Box>
        
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {formatTarget.type === 'curve' ? (
          // Curve formatting content
          <Box>
            {(() => {
              // Get the current curve format from chart's curveReferences
              let currentFormat = defaultCurveFormat
              
              if (formatTarget.curveId && chart) {
                const curveRef = chart.curveReferences.find(ref => {
                  const refId = `${ref.reference.fileType}:${ref.reference.curveName}`
                  return refId === formatTarget.curveId
                })
                
                if (curveRef?.styling) {
                  currentFormat = curveRef.styling
                }
              }

              return (
                <CurveFormatEditor
                  format={currentFormat}
                  onFormatChange={(newFormat) => {
                    if (formatTarget.curveId) {
                      onCurveFormatChange?.(formatTarget.chartId, formatTarget.curveId, newFormat)
                    }
                  }}
                />
              )
            })()}
            
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Chart: {chart.title || `Chart ${formatTarget.chartId}`}
            </Typography>
          </Box>
        ) : (
          // Axis configuration content  
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure axis properties, title, units, and range settings.
            </Typography>
            
            {formatTarget.axisId && (
              <AxisConfigEditor
                chartId={formatTarget.chartId}
                axisId={formatTarget.axisId}
                axisConfig={chart.yAxes?.[formatTarget.axisId]}
                onAxisConfigChange={(config) => {
                  onAxisConfigChange?.(formatTarget.chartId, formatTarget.axisId!, config)
                }}
              />
            )}
            
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Chart: {chart.title || `Chart ${formatTarget.chartId}`}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  )
} 